import { useState, useEffect } from "react";

type ContactModalProps = {
  open: boolean;
  onClose: () => void;
};

export function ContactModal({ open, onClose }: ContactModalProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [showEmails, setShowEmails] = useState(false);

  // Reset states when modal closes
  useEffect(() => {
    if (!open) {
      setLoading(false);
      setSuccess(false);
      setError(false);
      setShowEmails(false);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(false);
    const formData = new FormData(e.currentTarget);
    try {
      const res = await fetch("https://submit-form.com/ZNKCmcFwA", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(Object.fromEntries(formData.entries())),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSuccess(true);
      e.currentTarget.reset();
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-10"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl overflow-hidden rounded-2xl border border-thema-border-hairline bg-[#0E121A] shadow-[0_25px_80px_rgba(0,0,0,0.65)]"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-white/5 px-2 py-1 text-sm text-white transition hover:bg-white/10"
          onClick={onClose}
        >
          ×
        </button>

        <div className="p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto">
          <div className="mb-4 space-y-1.5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-thema-teal/80">Contact</p>
            <h3 className="font-serif text-3xl font-semibold text-thema-text">Contact the team</h3>
            <p className="text-sm text-thema-text-soft/80">
              We respond via email. Please include context or links where relevant.
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            {/* Left Part - Form or Success Message */}
            <div className={`flex-1 ${showEmails && !success ? "hidden lg:block" : ""}`}>
              {success ? (
                <div className="flex flex-col items-center justify-center rounded-lg border-2 border-green-500/60 bg-green-500/10 p-8 md:p-12 text-center animate-fade-in-up">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
                    <svg
                      className="h-8 w-8 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h4 className="mb-2 font-serif text-xl font-semibold text-thema-text">Success!</h4>
                  <p className="text-sm text-thema-text-soft/80">
                    Message received. A team member will follow up via email.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-xs font-mono uppercase tracking-[0.16em] text-thema-text-soft/70">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] px-4 py-3 text-sm text-thema-text placeholder:text-thema-text-soft/40 focus:border-thema-royal-blue/50 focus:outline-none focus:ring-1 focus:ring-thema-royal-blue/30 transition"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-xs font-mono uppercase tracking-[0.16em] text-thema-text-soft/70">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] px-4 py-3 text-sm text-thema-text placeholder:text-thema-text-soft/40 focus:border-thema-royal-blue/50 focus:outline-none focus:ring-1 focus:ring-thema-royal-blue/30 transition"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="company" className="block text-xs font-mono uppercase tracking-[0.16em] text-thema-text-soft/70">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] px-4 py-3 text-sm text-thema-text placeholder:text-thema-text-soft/40 focus:border-thema-royal-blue/50 focus:outline-none focus:ring-1 focus:ring-thema-royal-blue/30 transition"
                      placeholder="Your company (optional)"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-xs font-mono uppercase tracking-[0.16em] text-thema-text-soft/70">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      className="w-full rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] px-4 py-3 text-sm text-thema-text placeholder:text-thema-text-soft/40 focus:border-thema-royal-blue/50 focus:outline-none focus:ring-1 focus:ring-thema-royal-blue/30 transition resize-none"
                      placeholder="Your message..."
                    />
                  </div>

                  <div className="sticky bottom-0 bg-[#0E121A] pt-3 pb-2 lg:static lg:pt-0 lg:pb-0">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`mt-4 inline-flex w-full items-center justify-center rounded-md px-4 py-3 text-sm font-semibold text-white shadow-[0_6px_24px_rgba(77,183,247,0.35)] transition ${
                        error
                          ? "bg-red-500/90 hover:bg-red-500"
                          : "bg-thema-royal-blue hover:bg-thema-royal-blue/90"
                      } ${loading ? "cursor-not-allowed opacity-75" : ""}`}
                    >
                    {loading ? (
                      <>
                        <svg
                          className="mr-2 h-4 w-4 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending...
                      </>
                    ) : error ? (
                      <>
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        Error - Try again
                      </>
                    ) : (
                      "Send →"
                    )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Vertical Divider with "or" */}
            {!success && (
              <div className="hidden lg:flex relative items-center justify-center w-px">
                <div className="absolute inset-y-0 w-px bg-thema-border-hairline/60" />
                <div className="relative z-10 bg-[#0E121A] px-3">
                  <span className="text-xs font-mono uppercase tracking-[0.16em] text-thema-text-soft/60">or</span>
                </div>
              </div>
            )}

            {/* Right Part - Email Cards */}
            <div className="flex-1 space-y-4">
              {/* Mobile toggle */}
              {!success && (
                <div className="lg:hidden">
                  <button
                    type="button"
                    className="w-full rounded-md border border-thema-border-hairline/60 bg-[#0A0F16] px-4 py-3 text-sm font-medium text-thema-text-soft hover:bg-white/5 transition"
                    onClick={() => setShowEmails((v) => !v)}
                  >
                    {showEmails ? "Back to form" : "Prefer to email directly?"}
                  </button>
                </div>
              )}

              {/* Email cards */}
              <div className={`${showEmails ? "block" : "hidden"} lg:block space-y-4`}>
                

                <div className="space-y-2 rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-thema-text">Aamir</p>
                    {/* <p className="text-xs text-thema-text-soft/75">Research / Markets</p> */}
                  </div>
                </div>
                <p className="mt-2 text-xs font-mono text-thema-text-soft/75">aamir@thema.financial</p>
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = "mailto:aamir@thema.financial";
                  }}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-md bg-thema-royal-blue px-3 py-2 text-xs font-semibold text-white shadow-[0_6px_24px_rgba(77,183,247,0.35)] transition hover:bg-thema-royal-blue/90"
                >
                  Email Aamir →
                </button>
              </div>
              <div className="space-y-2 rounded-lg border border-thema-border-hairline/60 bg-[#0A0F16] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-thema-text">Brian</p>
                    {/* <p className="text-xs text-thema-text-soft/75">Protocol / Engineering</p> */}
                  </div>
                </div>
                <p className="mt-2 text-xs font-mono text-thema-text-soft/75">brian@thema.financial</p>
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = "mailto:brian@thema.financial";
                  }}
                  className="mt-2 inline-flex w-full items-center justify-center rounded-md bg-thema-royal-blue px-3 py-2 text-xs font-semibold text-white shadow-[0_6px_24px_rgba(77,183,247,0.35)] transition hover:bg-thema-royal-blue/90"
                >
                  Email Brian →
                </button>
              </div>

                {/* Disclaimer */}
                <div className="mt-6 max-w-md space-y-2 pt-4 border-t border-thema-border-hairline/30 font-mono opacity-40">
                <p className="text-[10px] leading-relaxed italic text-thema-text-soft/60">
                  **Thema may run limited strategies with proprietary capital for validation based on v0.1-beta: an early-stage protocol design and tooling. Content reflects architectural intent, not operational commitments.
                </p>
                <p className="text-[10px] leading-relaxed italic text-thema-text-soft/60">
                  **Third-party tooling access, if any, is experimental and provided only through contractually licensed operators subject to applicable regulations. Thema makes no claims regarding returns, risk, or market outcomes. Not an offer, advisory service, or solicitation.
                </p>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

